import * as RadixPopover from '@radix-ui/react-popover';
import { type PopoverPortalProps } from '@radix-ui/react-popover';
import { memo } from 'react';

import { usePortalStore } from '@air/primitive-portal';
import { tailwindVariants } from '@air/utils-tailwind';

const popover = tailwindVariants({
  base: 'z-portal max-h-[calc(var(--radix-popover-content-available-height)-32px)] w-full overflow-y-auto rounded-4 bg-surface-1 shadow-popover outline-none radix-side-bottom:animate-slide-up radix-side-top:animate-slide-down dark:border dark:border-grey-5',
  defaultVariants: {
    size: 'small',
  },
  variants: {
    size: {
      large: 'min-w-[240px] p-2',
      small: 'min-w-[216px] p-1.5',
    },
  },
});

export type PopoverSize = 'small' | 'large';

export type PopoverProps = RadixPopover.PopoverProps &
  RadixPopover.PopoverContentProps & {
    size?: PopoverSize;
    trigger: RadixPopover.PopoverTriggerProps['children'];
    container?: PopoverPortalProps['container'];
  };

export const Popover = memo(
  ({
    align = 'start',
    children,
    className,
    collisionPadding = 8,
    container,
    defaultOpen,
    modal,
    onOpenChange,
    open,
    side = 'bottom',
    sideOffset = 8,
    size = 'small',
    trigger,
    ...restOfProps
  }: PopoverProps) => {
    const portal = usePortalStore((state) => state.portal);

    return (
      <RadixPopover.Root defaultOpen={defaultOpen} modal={modal} onOpenChange={onOpenChange} open={open}>
        <RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
        <RadixPopover.Portal container={container || portal}>
          <RadixPopover.Content
            align={align}
            className={popover({ class: className, size })}
            collisionPadding={collisionPadding}
            side={side}
            sideOffset={sideOffset}
            {...restOfProps}
          >
            {children}
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  },
);

Popover.displayName = 'Popover';
